import { DateTime } from 'luxon';
import { datetime } from 'rrule';

function parseTimeParts(
  time: string,
): { hours: number; minutes: number } | null {
  if (!time) {
    return null;
  }

  const normalized = time.trim();

  const hhmmMatch = normalized.match(/^(\d{1,2}):(\d{2})$/);
  if (hhmmMatch) {
    const hours = Number(hhmmMatch[1]);
    const minutes = Number(hhmmMatch[2]);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
      return null;
    }

    return { hours, minutes };
  }

  const ampmMatch = normalized.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (ampmMatch) {
    let hours = Number(ampmMatch[1]);
    const minutes = Number(ampmMatch[2]);
    const period = ampmMatch[3].toUpperCase();

    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
      return null;
    }

    if (period === 'PM' && hours < 12) {
      hours += 12;
    }

    if (period === 'AM' && hours === 12) {
      hours = 0;
    }

    return { hours, minutes };
  }

  return null;
}

/**
 * Converts a time string in "HH:mm" format to a 12-hour format with AM/PM
 * @param time - Time string in "HH:mm" format (24-hour)
 * @returns Formatted time string in "H:mm AM/PM" format
 */
export function formatTimeString(time: string): string {
  const timeParts = parseTimeParts(time);
  if (!timeParts) {
    return '';
  }

  const { hours, minutes } = timeParts;
  const period = hours >= 12 ? 'PM' : 'AM';
  const formattedHours = hours % 12 || 12;
  return `${formattedHours}:${minutes.toString().padStart(2, '0')} ${period}`;
}

/**
 * Adjusts a session date by setting the time and applying timezone offset
 * Handles cases where time is not properly set in the session data by combining
 * the session date with the time and applying the specified timezone
 * @param session - Session object containing date, time, and timezone identifier
 * @param session.date - The base date for the session
 * @param session.time - Time string in "HH:mm" format (24-hour)
 * @param session.tzid - IANA timezone identifier (defaults to 'Asia/Colombo' if not provided)
 * @returns Adjusted Date object with the correct time and timezone applied
 */
export function getAdjustedDate(session: {
  date: Date;
  time: string;
  tzid: string;
}) {
  const timeParts = parseTimeParts(session.time);
  if (!timeParts) {
    return new Date(session.date);
  }

  const { hours, minutes } = timeParts;
  const tzid = session.tzid ?? 'Asia/Colombo';
  const baseUtc = DateTime.fromJSDate(session.date, { zone: 'utc' });

  return DateTime.fromObject(
    {
      year: baseUtc.year,
      month: baseUtc.month,
      day: baseUtc.day,
      hour: hours,
      minute: minutes,
      second: 0,
      millisecond: 0,
    },
    { zone: tzid },
  ).toJSDate();
}

/**
 * Converts a JavaScript Date object to an RRule-compatible datetime object
 * @param date - The JavaScript Date object to convert
 * @param partial - If true, returns only year, month, and date without time. Defaults to false
 * @returns An RRule-compatible datetime object
 */
export function datetimeFromJSDate(date: Date, partial = false) {
  if (partial)
    return datetime(
      date.getUTCFullYear(),
      date.getUTCMonth() + 1,
      date.getUTCDate(),
    );

  return datetime(
    date.getUTCFullYear(),
    date.getUTCMonth() + 1,
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
  );
}

/**
 * Converts an RRule-compatible datetime object to a JavaScript Date object
 * Timezone is corrected to the local timezone using Luxon
 * @param date - The JavaScript Date object to convert
 * @returns A timezone-corrected JavaScript Date object in local time
 */
export function tzCorrectedDate(date: Date) {
  return DateTime.fromJSDate(date)
    .toUTC()
    .setZone('local', { keepLocalTime: true })
    .toJSDate();
}
